import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import cx from 'classnames';

import { useAuth0 } from '@auth0/auth0-react';
import { stringToJSON } from '~helpers/common';

import { useOrder } from '~components/listing/useOrder';
import FiltersButton from '~components/listing/filters/button';
import Sort from '~components/listing/sort';
import ResultsCount from '~components/listing/results-count';
import { useListing } from '~components/listing/useListing';
import MapToggle from '~components/map/map-toggle/map-toggle';

import ServerContext from '~src/serverContext';
import { useFilters } from './filters/useFilters';
import Filters from './filters';

import ResultsList from './results-list';
import ResultsMap from './results-map';
import AddOfferButton from './add-offer';

import LocationContext from '../locationContext';
import { ORDER } from './order';

import { searchCriteria } from './searchCriteria';

import '~components/listing/style.scss';
import { SidebarCities } from '~components/footer/cities';
import { LocationSearch } from '~components/forms/location';

import BottomNav from '~components/bottom-bar/BottomNav';
import Button from '~components/button';
import { RxCross2 } from "react-icons/rx";

const LISTING_ITEMS = 12;

const WorkersList = ({ isCityPage = false, onMapViewChange }) => {
  const history = useHistory();
  const intl = useIntl();
  const cache = useContext(ServerContext);

  const domainConfig = useSelector((state) => state.domainConfig);

  const timeoutRef = useRef();

  const { locationDetails, pricingDetails, isLoadingLocation, locationTouched, onChangeLocation } = useContext(LocationContext);
  
  const { isAuthenticated } = useAuth0();

  const [order, selectedOrder, setOrder, orderTouched] = useOrder(ORDER);
  const { filters, updateFilters, filtersTouched, areFiltersInitialized } = useFilters();

  const [delayedRender, setDelayedRender] = useState(false);
  const [isShowingMap, setIsShowingMap] = useState(false);
  const [isLoadedOnce, setIsLoadedOnce] = useState(false);
  const [isSortVisible, setIsSortVisible] = useState(false);

  const defaultCriteria = useRef(
    searchCriteria({
      location: locationDetails,
      currency: pricingDetails?.currency,
      pagination: {
        limit: LISTING_ITEMS,
        offset: 0,
      },
      order: selectedOrder,
      filters,
    })
  ).current;

  const parseResult = (r) => {
    if (!r) {
      return null;
    }

    return {
      personId: r.id,
      firstName: r.first_name,
      profilePicture: stringToJSON(r.profile_picture),
      lastLogin: r.last_login,
      registerDate: r.registered_date,
      isOnline: r.is_online,
      personalNote: r.personal_note,
      verification: [
        {
          type: 'email',
          isVerified: (r.verification.find((item) => item.type === 'EMAIL') || {}).is_verified || false,
        },
        {
          type: 'sms',
          isVerified: (r.verification.find((item) => item.type === 'SMS') || {}).is_verified || false,
        },
      ],
      score: r.score,
      price: r.price,
      location: {
        locationId: r.location.place_id,
        place_id: r.location.place_id,
        city: r.location.city,
        slug: r.location.slug,
        country: r.location.country,
        latitude: r.location.latitude,
        longitude: r.location.longitude,
      },
      experience: r.experience,
      days: r.days,
      published: r.published,
    };
  };

  const { results, count, offset, isLoadingResults, isLoadingCount, isLoadingMore, updateListing, reorder, showMore } =
    useListing({
      criteria: defaultCriteria,
      matchRequestName: 'workers',
      countRequestName: 'workers',
      matchMockFileName: 'workers/get-workers-by-criteria',
      countMockFileName: 'workers/count-workers-by-criteria',
      itemsPerPage: LISTING_ITEMS,
      parseResult,
    });

  const [desktopFiltersVisible, setDesktopFiltersVisible] = useState(true);
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

  // Add mounted ref
  const isMounted = useRef(true);
  
  // Set up cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Modify state updates to check mounted status
  const toggleMap = () => {
    if (!isMounted.current) return;
    
    if (isShowingMap) {
      setIsShowingMap(false);
      setDesktopFiltersVisible(true);
      if (onMapViewChange) {
        onMapViewChange(false);
      }
      return;
    }

    if (!locationDetails || !locationDetails.locationId) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setDelayedRender(true);
    setIsShowingMap(true);
    setDesktopFiltersVisible(false);
    if (onMapViewChange) {
      onMapViewChange(true);
    }

    const newCriteria = searchCriteria({
      location: locationDetails,
      currency: pricingDetails?.currency,
      order: selectedOrder,
      filters,
    });

    const updatePromise = updateListing(newCriteria, !isAuthenticated);
    timeoutRef.current = setTimeout(() => {
      if (isMounted.current) {
        setDelayedRender(false);
      }
    }, 0);

    return () => {
      if (updatePromise && typeof updatePromise.cancel === 'function') {
        updatePromise.cancel();
      }
    };
  };

  useEffect(() => {
    if (!orderTouched) {
      return;
    }

    const newCriteria = searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
      currency: pricingDetails?.currency,
      filters,
      pagination: {
        limit: LISTING_ITEMS,
        offset: 0,
      },
      order: selectedOrder,
    });

    const reorderPromise = reorder(newCriteria);

    return () => {
      if (reorderPromise && typeof reorderPromise.cancel === 'function') {
        reorderPromise.cancel();
      }
    };
  }, [selectedOrder]);

  useEffect(() => {
    if (!filtersTouched) {
      return;
    }

    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: 0,
        }
      : null;

    const newCriteria = searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
      currency: pricingDetails?.currency,
      filters,
      order: selectedOrder,
      pagination,
    });

    // updateListing(newCriteria);
    const updatePromise = updateListing(newCriteria);
    
    return () => {
      if (updatePromise && typeof updatePromise.cancel === 'function') {
        updatePromise.cancel();
      }
    };
  }, [filters]);

  useEffect(() => {
    if (!locationTouched || !locationDetails || !locationDetails.locationId || isLoadingLocation || !areFiltersInitialized) {
      return;
    }

    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: 0,
        }
      : null;

    const nFilters = { ...filters };
    if (filters.price === null) {
      nFilters.price = pricingDetails.maxPrice;
    }

    const newCriteria = searchCriteria({
      location: locationDetails,
      currency: pricingDetails?.currency,
      pagination,
      order: selectedOrder,
      filters: nFilters,
    });

    // Store the update function call in a variable
    const updatePromise = updateListing(newCriteria, !isAuthenticated && !isLoadedOnce);
    setIsLoadedOnce(true);

    const timeoutId = setTimeout(() => setDelayedRender(false), 0);
    
    return () => {
      // Clear the timeout
      clearTimeout(timeoutId);
      // Cancel any pending update if component unmounts
      if (updatePromise && typeof updatePromise.cancel === 'function') {
        updatePromise.cancel();
      }
    };

  }, [isLoadingLocation, areFiltersInitialized]);

  useEffect(() => {
    if (isLoadingLocation) {
      setDelayedRender(true);
    }
  }, [isLoadingLocation]);

  useEffect(() => {
    if (locationDetails?.city) {
      cache.del('serverCityName');
    }
  }, [locationDetails?.city]);

  useEffect(() => {
    return () => {
      if (cache && typeof cache.del === 'function' && isMounted.current) {
        cache.del(`cityWorkers${slug}`);
        cache.del(`cityWorkersCount${slug}`);
      }
    };
  }, [slug]);

  const handleGetListedAsCleanerClick = () => {
    history.push('/intake/worker'); 
  };

  const handleMakeAccountClick = () => {
    history.push('/intake/client/full'); 
  };

  const getInitialCount = () => {
    const cityWorkers = cache.get('cityWorkers')
    // console.log(cityWorkers)
    if (isCityPage && cityWorkers) {
      return cityWorkers.count;
    }
    return count;
  };

  // ssr section start
  const serverLocation = isCityPage ? cache.get('serverLocation') : null;
  const initialSlug = serverLocation?.slug || '';
  const [slug, setSlug] = useState(initialSlug);

  // Get server cache data synchronously for SSR
  const serverWorkers = isCityPage ? cache.get(`cityWorkers${initialSlug}`) : null;
  const serverWorkersCount = isCityPage ? cache.get(`cityWorkersCount${initialSlug}`) : null;

  // Process server cache data through parseResult
  const serverMatches = serverWorkers ? serverWorkers.map(parseResult) : [];

  // Use matches prop first, then fall back to server cache if available
  // Ensure we always have an array, even if both results and serverMatches are empty
  const displayMatches = results.length > 0 ? results : serverMatches;
  const displayCount = count || serverWorkersCount || 0;

  // Clean up server cache when real data arrives
  useEffect(() => {
    if (isCityPage && results.length > 0) {
      cache.del(`cityWorkers${slug}`);
      cache.del(`cityWorkersCount${slug}`);
    }
  }, [results, isCityPage, slug]);

  // Update slug when URL changes
  useEffect(() => {
    if (isCityPage) {
      const pathSlug = window.location.pathname.substring(1).toLowerCase().replaceAll(/\/$/g, '');
      setSlug(pathSlug);
    }
  }, [isCityPage]);
  // ssr section end

  return (
    <>
    <BottomNav 
      onFilterClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
      onSortClick={() => {
        if (!isLoadingLocation && !isLoadingResults && !isLoadingCount && !delayedRender) {
          setIsSortVisible(!isSortVisible);
        }
      }}
      onMapClick={toggleMap}
      isMapActive={isShowingMap} 
      isMapDisabled={
        !locationDetails ||
        !locationDetails.locationId ||
        isLoadingLocation ||
        isLoadingResults ||
        isLoadingCount ||
        !count
      }
      isLoading={isLoadingLocation || isLoadingResults || isLoadingCount || delayedRender}
      locationId={locationDetails?.locationId}
    />
    <div className="c-matches__container">
      <aside
        className={cx('c-page__sidebar c-page__sidebar--matches c-page__sidebar--tablet-hidden', {
          'l-hidden': !desktopFiltersVisible,
        })}
      >
        <div className="c-sidebar__title">
          {intl.formatMessage({
            id: 'containers.workers.sidebar.title',
            defaultMessage: 'Your wishes',
          })}
        </div>
        <Filters
          values={filters}
          onChange={updateFilters}
          showMobileFilters={mobileFiltersVisible}
          showDesktopFilters={desktopFiltersVisible}
          isLoading={isLoadingLocation || delayedRender}
        />
        
        {isCityPage && <SidebarCities displayNeighborhoods={true} />}
        
      </aside>
      <div className={cx('c-matches__list c-page__main', { 'c-matches__list--map-view': isShowingMap, 'c-matches__list--city-page': isCityPage })}>
        
        { !isShowingMap ? (
          <div className="srch-wrap">
            <div className="srch-lst">
              {isCityPage && 
                <>
                  <div className="srch-city-title">
                    <h1>
                      {intl.formatMessage({
                        id: 'containers.city.seo.location.title',
                        defaultMessage: 'Huishoudelijke hulp {city}',
                      },
                      {
                        city: locationDetails?.city ?? locationDetails?.country ?? cache.get('serverCityName'),
                      })}
                    </h1>
                  </div>
                  <div className="srch-city-sub-title">
                    {intl.formatMessage({
                      id: 'containers.city.seo.location.sub-title',
                      defaultMessage: 'Are you looking for a cleaner in {city}? a trustyworthy cleaner you can find easily on {domain}, {worker_count} cleaners in {city} are available for you.',
                    },
                    {
                      city: locationDetails?.city ?? locationDetails?.country ?? cache.get('serverCityName'),
                      domain: domainConfig.domain,
                      worker_count: getInitialCount(),
                    })}
                  </div>
                </>
              }

              {!isCityPage && 
                <div className="srch-title">
                  {intl.formatMessage({
                    id: 'containers.workers.sidebar.subTitle',
                    defaultMessage: 'Fill in your postcode or address to see the cleaners that work at your address',
                  })}
                </div>
              }
                
                <LocationSearch
                  isDataLoading={isLoadingLocation || delayedRender}
                  location={!isCityPage ? locationDetails : {}}
                  placeholder={intl.formatMessage({
                    id: 'containers.workers.subheader.address',
                    defaultMessage: 'Address',
                  })}
                  name="address"
                  className="c-autocomplete--focus-disabled" 
                  onSubmit={(loc) => {
                    if (loc && loc.place_id) {
                      history.push({
                        pathname: '/workers',
                        search: `?locationId=${loc.place_id}`,
                        state: {
                          location: {
                            locationId: loc.place_id,
                            address: loc.address
                          }
                        }
                      });
                      if (!isCityPage && onChangeLocation) {
                        onChangeLocation(loc.place_id);
                      }
                    }
                  }}
                />
                {isCityPage && 
                  <div className="city_cltd-link">
                    <a onClick={handleGetListedAsCleanerClick}>{intl.formatMessage({
                        id: 'containers.workers.get-listed-as-cleaner',
                        defaultMessage: 'Get Listed as cleaner',
                      })}</a>
                  </div>
                }
              </div>
              {isCityPage && 
                <div className="right-free-service">
                  <h3>{intl.formatMessage({
                        id: 'containers.city.seo.free-service.title',
                        defaultMessage: 'Free service:',
                      })}</h3>
                  <p>{intl.formatMessage({
                        id: 'containers.city.seo.free-service.sub-title',
                        defaultMessage: 'Be the first to know new cleaners have registered by making an account for free',
                      })}</p>
                  <Button className="c-btn c-card__button" onClick={handleMakeAccountClick}>
                    {intl.formatMessage({ id: 'containers.city.seo.free-service.button', defaultMessage: 'Make Account' })}
                  </Button>
                </div>
              }
          </div>
        ) : (
          <Button onClick={toggleMap} className="c-map-close">
            <RxCross2 size={24}/>
          </Button>
        )}
        <div className="c-matches__list-header">
          <div className="c-matches__list-header-left">
            <ResultsCount count={count} isLoading={isLoadingLocation || isLoadingCount || delayedRender} />
            <FiltersButton
              className={cx({ 'c-filters-button--map-visible': isShowingMap })}
              isLoading={isLoadingLocation || isLoadingResults || delayedRender}
              onClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
            />
          </div>
          
          <div className="c-matches__list-header-right">
            
            <MapToggle isLoading={isLoadingLocation || isLoadingResults || isLoadingCount || delayedRender} 
                          isShowingMap={isShowingMap}
                          disabled={
                            !locationDetails ||
                            !locationDetails.locationId ||
                            isLoadingLocation ||
                            isLoadingResults ||
                            isLoadingCount ||
                            !count
                          }
                          onClick={toggleMap}
            />
            {(!isLoadingLocation && !count) || isShowingMap ? null : (
              <Sort
                order={order}
                value={selectedOrder}
                isLoading={isLoadingLocation || isLoadingResults || isLoadingCount || delayedRender}
                onChange={setOrder}
                isVisible={isSortVisible}
                onVisibleChange={setIsSortVisible}
              />
            )}
          </div>
        </div>

        {!isShowingMap ? (
          <ResultsList
            // matches={results} // without ssr
            // count={count} // without ssr
            key={`${locationDetails?.locationId}_${Date.now()}`}
            matches={displayMatches}
            count={displayCount}
            isCityPage={isCityPage}
            cityName={locationDetails?.city || locationDetails?.country}
            citySlug={locationDetails?.slug}
            locationId={locationDetails?.locationId}
            isLoading={isLoadingResults || isLoadingLocation || delayedRender}
            isLoadingMore={isLoadingMore}
            onShowMore={() =>
              showMore(
                searchCriteria({
                  ...defaultCriteria,
                  location: locationDetails,
                  currency: pricingDetails?.currency,
                  filters,
                  order: selectedOrder,
                })
              )}
          />
        ) : (
          <ResultsMap
            matches={results}
            isLoading={isLoadingResults || isLoadingCount || isLoadingLocation || delayedRender}
            locationId={locationDetails?.locationId}
          />
        )}

        {/* {count > 0 &&
        offset + LISTING_ITEMS >= count &&
        !isLoadingMore &&
        !isLoadingResults &&
        !isLoadingCount &&
        !isLoadingLocation &&
        !delayedRender ? (
          <AddOfferButton
            destination={`/intake/client${
              locationDetails?.locationId ? `?locationId=${locationDetails?.locationId}` : ''
            }&showFields=true`}
          />
        ) : null} */}
      </div>
    </div>
    </>
  );
};

export default WorkersList;
